import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import {
  BulkManifestDiscrepanciesQuery,
  DiscrepancyKind,
  DocumentType,
} from "generated-graphql/graphql";
import { useCtrlWheelHorizontalScroll } from "hooks/useCtrlWheelHorizontalScroll";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useFireOnce } from "hooks/useFireOnce";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { CREATE_EXCEPTION_REPORT_SUBMISSION, PROCESSED_MANIFESTS_QUERY } from "./api";
import { CloseNeverInEManifestDialogSlot } from "./CloseNeverInEManifestDialog";
import {
  type ComplianceItem,
  type ComplianceItemMissing,
  getDeadlineBand,
  mapEpaCorrectionDiscrepancy,
  mapMissingEManifestDiscrepancy,
} from "./CompliancePane.helpers";
import { DataSourceConflictsPane } from "./DataSourceConflictsPane";
import {
  buildFieldGroups,
  findWasteLineMatchingTarget,
  manifestsWithSettledWasteLineMatch,
  summarizeUnmatchableLines,
  type UnmatchableLinesSummary,
  unmatchableLinesDetail,
  unmatchableLinesHeadline,
} from "./DataSourceConflictsPane.helpers";
import {
  type ActionKey,
  computeActionEligibility,
  formatManifestContextLine,
  formatSkipSummary,
  getDiscrepancyStatus,
  getStatusBreakdownLabel,
  isComplianceDiscrepancy,
  sortTerminalLast,
  vendorDataUpdatedAlert,
} from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancySection } from "./DiscrepancySection";
import { EpaCorrectionsPane } from "./EpaCorrectionsPane";
import { FieldIssuesPane } from "./FieldIssuesPane";
import { ManifestBlockControl, ManifestBlockedChip } from "./ManifestBlockControl";
import { ManifestCommentsPopover } from "./ManifestCommentsPopover";
import { MissingEManifestPane } from "./MissingEManifestPane";
import { ProofOfContactDialog } from "./ProofOfContactDialog";
import { ResolveDiscrepanciesDialog } from "./ResolveDiscrepanciesDialog";
import { RowActionButton } from "./RowActionButton";
import { useRangeSelection } from "./useRangeSelection";
import { type WasteLineMatchingTarget, WasteLinePairingDialog } from "./WasteLinePairingDialog";

export const BULK_MANIFEST_DISCREPANCIES_QUERY = gql(`
  query BulkManifestDiscrepancies($processedManifestIds: [ID!]!) {
    bulkManifestDiscrepancies(processedManifestIds: $processedManifestIds) {
      id
      processedManifestId
      manifestTrackingNumber
      facilityName
      epaId
      wasteLineUnifiedId
      wasteLineNumber
      wasteLineDotDescription
      field
      isDot
      kind
      status
      eManifestValue
      vendorValue
      manifestDocumentValue
      vendorDiscrepant
      documentDiscrepant
      vendorAlignmentSuspect
      vendorWasteLinesMatched
      vendorWasteLinesUnmatched
      sourceDocumentId
      sourceDocumentFileExtension
      escalation
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
      heldBackReason
      createdAt
      ruleKey
      wasteManifestCorrectionRequest {
        id
        status
        requestDate
        industryRespondedAt
        initiatorRole
        sections
      }
    }
  }
`);

const UPDATE_DISCREPANCY_VENDOR_DATA = gql(`
  mutation UpdateDiscrepancyVendorData($discrepancyIds: [ID!]!, $note: String) {
    updateDiscrepancyVendorData(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

const RESOLVE_DISCREPANCIES_EXTERNALLY = gql(`
  mutation ResolveDiscrepanciesExternally($discrepancyIds: [ID!]!, $note: String) {
    resolveDiscrepanciesExternally(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

const REOPEN_PENDING_DISCREPANCIES = gql(`
  mutation ReopenPendingDiscrepancies($discrepancyIds: [ID!]!) {
    reopenPendingDiscrepancies(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

export const UNDO_RESOLVED_DISCREPANCIES = gql(`
  mutation UndoResolvedDiscrepancies($discrepancyIds: [ID!]!) {
    undoResolvedDiscrepancies(discrepancyIds: $discrepancyIds) {
      id
      processedManifestId
      field
      status
      eManifestValue
      vendorValue
      vendorDiscrepant
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

export const DISMISS_DISCREPANCIES = gql(`
  mutation DismissDiscrepancies($discrepancyIds: [ID!]!, $note: String) {
    dismissDiscrepancies(discrepancyIds: $discrepancyIds, note: $note) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolvedBy {
        id
        person {
          first
          last
        }
      }
      resolutionPath
      resolutionNote
    }
  }
`);

export const COMPLIANCE_MANIFEST_DATA_QUERY = gql(`
  query ComplianceManifestData($search: String, $page: Int, $pageSize: Int) {
    processedManifests(search: $search, page: $page, pageSize: $pageSize) {
      items {
        id
        shippedDate
        desFacilityName
        transporterContactedAt
        transporterContactedBy
        documents {
          document {
            id
            documentType
          }
        }
        exceptionReportSubmission {
          id
          title
          status
        }
      }
    }
  }
`);

// rem-based so it tracks root font-size (≈37px at 16px root) — the small-table header height,
// matched here so the source document preview panel's header lines up with the first data row.
const STICKY_COLUMN_HEADER_HEIGHT = "2.3125rem";

/**
 * Each section's explanatory paragraph. `px` matches the table cells' own inset
 * below it, so the sentence starts on the same x as the first column. `maxWidth`
 * is a reading measure: the cards run the full dialog and a sentence set to that
 * width is one very long line.
 */
const SECTION_DESCRIPTION_SX = { px: 2, pt: 1.5, pb: 1.5, maxWidth: "80ch" } as const;

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/**
 * Says, above the list, that some of these conflicts compare two different
 * wastes.
 *
 * The same fact is on each affected row, but a reviewer working down the list
 * has usually chosen an action before expanding anything, and the rows this
 * applies to look ordinary until then. Renders nothing when every row's waste
 * lines pair by number, which is the usual case.
 *
 * Stays on screen as a settled notice once a pairing is recorded, in both of
 * the shapes that can mean. Right after a save the rows are still marked and
 * still hold the findings computed against the line numbers. After the next
 * run they are not marked at all, because every line is decided. The notice is
 * the only way back into the matching screen, so it cannot go away just
 * because the thing it warned about was answered.
 */
function UnmatchableLinesNotice({
  summary,
  settledManifestIds,
  justMatchedIds,
  onMatchLines,
}: {
  summary: UnmatchableLinesSummary | null;
  /** Manifests whose recorded pairing the last manifest run has already applied. */
  settledManifestIds: readonly string[];
  /** Manifests matched since this dialog opened, whose rows have not been recomputed yet. */
  justMatchedIds: ReadonlySet<string>;
  /** Absent when nothing in this list has a vendor-side count mismatch. */
  onMatchLines?: () => void;
}) {
  const changeMatching = onMatchLines ? (
    <Button color="inherit" size="small" onClick={onMatchLines}>
      Change matching
    </Button>
  ) : undefined;

  const settledNotice = (detail: string) => (
    <Alert severity="info" sx={{ mx: 2, mb: 1.5 }} action={changeMatching}>
      <AlertTitle>Waste lines matched</AlertTitle>
      {detail}
    </Alert>
  );

  // Saved earlier and already applied, so nothing is marked any more.
  if (summary == null) {
    if (settledManifestIds.length === 0) return null;
    return settledNotice("The findings below compare the lines as they were matched.");
  }

  // Saved in this session, and the run that acts on it has not been through.
  if (summary.processedManifestIds.every((id) => justMatchedIds.has(id))) {
    return settledNotice("The findings below are recalculated on the next manifest run.");
  }

  return (
    <Alert
      severity="warning"
      sx={{ mx: 2, mb: 1.5 }}
      action={
        onMatchLines ? (
          <Button color="inherit" size="small" onClick={onMatchLines}>
            Match waste lines
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{unmatchableLinesHeadline(summary)}</AlertTitle>
      {unmatchableLinesDetail(summary)}
    </Alert>
  );
}

/**
 * Per-action definitions shared by the resolve modal's options. Kept as data
 * so the Data Source Conflicts and Data Quality Issues sections build the
 * same modal, minus "Correct Vendor Data" for the latter — a Data Quality
 * Issue's eManifestValue is the flagged (invalid) value itself, so copying it
 * into vendor data would propagate the problem and falsely mark it resolved.
 */
const RESOLVE_ACTION_DEFS: Record<ActionKey, { label: string; description: string }> = {
  updateVendorData: {
    label: "Correct Vendor Data",
    description:
      "Overwrite vendor data with e-Manifest values. Rows whose manifest document also disagrees stay Pending Review.",
  },
  resolveExternally: {
    label: "Resolve in e-Manifest",
    description:
      'Mark as being resolved in e-Manifest (e.g. updating the value in RCRAInfo). These will move to "Pending" until the change propagates back through the system.',
  },
  nonDiscrepancy: {
    label: "Non-Discrepancy",
    description:
      "Mark as non-discrepancies. The difference is expected and will be hidden from view.",
  },
  cantDetermine: {
    label: "Can't Resolve — Mark as Blocked",
    description:
      "Block the whole manifest with a reason. It leaves the actionable queue (and is unassigned) until someone unblocks it; nothing is resolved.",
  },
};

/**
 * The row id "apply to others" matches against — only meaningful when the
 * resolve modal is acting on exactly one row (see ResolveDiscrepanciesDialog).
 */
function getSingleDiscrepancyId(target: { rows: Discrepancy[] } | null): string | undefined {
  const rows = target?.rows ?? [];
  return rows.length === 1 ? rows[0].id : undefined;
}

/** "1 discrepancy <suffix>" vs "N discrepancies <suffix>" for a batch action's success toast. */
function countedDiscrepancyAlert(count: number, suffix: string): string {
  const noun = count === 1 ? "discrepancy" : "discrepancies";
  return `${count} ${noun} ${suffix}`;
}

/**
 * The blocked chip, where there is one manifest a staff user could block. Its
 * own component so the gating does not add branches to DiscrepancyReviewDialog.
 */
function HeaderBlockedChip({
  isStaff,
  isSingleManifest,
  processedManifestId,
}: {
  isStaff: boolean;
  isSingleManifest: boolean;
  processedManifestId: string;
}) {
  if (!isStaff || !isSingleManifest) return null;
  return <ManifestBlockedChip processedManifestId={processedManifestId} />;
}

/** The header subtitle naming the shipment. Renders nothing when there's no line to show. */
function HeaderContextLine({
  text,
  trailing,
}: {
  text: string | null;
  /** The blocked chip, which belongs with the manifest's identity rather than among the header's buttons. */
  trailing?: React.ReactNode;
}) {
  if (!text && !trailing) return null;
  return (
    // px matches the dialog title's own inset — the content area contributes
    // none, so this line sets its own to stay under the title.
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 3 }}
    >
      {text && (
        <Typography variant="caption" color="text.secondary">
          {text}
        </Typography>
      )}
      {trailing}
    </Stack>
  );
}

/**
 * Title-bar controls for the single-manifest case: staff comments and a
 * "View document" button opening the source document in the preview pane.
 * Renders nothing in bulk/line-item mode, which has no one manifest to act on.
 */
function DialogTitleBarActions({
  isStaff,
  isSingleManifest,
  processedManifestId,
  manifestTrackingNumber,
  viewDocumentTarget,
  onViewDocument,
  markBlockedOpen,
  markBlockedInitialNote,
  onOpenMarkBlocked,
  onCloseMarkBlocked,
  onBlocked,
}: {
  isStaff: boolean;
  isSingleManifest: boolean;
  processedManifestId: string;
  manifestTrackingNumber?: string;
  viewDocumentTarget: { documentId: string; fileExtension: string | null | undefined } | null;
  onViewDocument: (documentId: string, fileExtension: string | null | undefined) => void;
  markBlockedOpen: boolean;
  markBlockedInitialNote?: string | null;
  onOpenMarkBlocked: () => void;
  onCloseMarkBlocked: () => void;
  onBlocked?: () => void;
}) {
  if (!isSingleManifest) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <RowActionButton
        blocked={!viewDocumentTarget}
        reason={viewDocumentTarget ? "" : "No document attached to this manifest"}
        startIcon={<DescriptionOutlinedIcon />}
        onClick={() => {
          if (!viewDocumentTarget) return;
          onViewDocument(viewDocumentTarget.documentId, viewDocumentTarget.fileExtension);
        }}
      >
        View document
      </RowActionButton>
      {isStaff && (
        <ManifestCommentsPopover processedManifestId={processedManifestId} appearance="button" />
      )}
      {isStaff && (
        <ManifestBlockControl
          processedManifestId={processedManifestId}
          manifestTrackingNumber={manifestTrackingNumber}
          markBlockedOpen={markBlockedOpen}
          markBlockedInitialNote={markBlockedInitialNote}
          onOpenMarkBlocked={onOpenMarkBlocked}
          onCloseMarkBlocked={onCloseMarkBlocked}
          onBlocked={onBlocked}
        />
      )}
    </Stack>
  );
}

function DiscrepancyBulkActionBar({
  totalSelected,
  loading,
  onOpen,
}: {
  totalSelected: number;
  loading: boolean;
  onOpen: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.5 }}>
      <Tooltip
        title={totalSelected === 0 ? "Select discrepancies to resolve them in bulk" : ""}
        placement="top"
      >
        <span>
          <Button
            variant="outlined"
            size="small"
            color="staff"
            startIcon={<TaskAltIcon />}
            disabled={totalSelected === 0 || loading}
            onClick={onOpen}
            // The label grows by a digit as rows are selected; without this it
            // wraps or clips inside the action bar's flex row.
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Resolve Selected{totalSelected > 0 ? ` (${totalSelected})` : ""}…
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}

export type DiscrepancySectionKey = "epaCorrection" | "eManifestMissing" | "field" | "fieldIssue";

interface DiscrepancyReviewDialogProps {
  processedManifestIds: string[];
  manifestTrackingNumber?: string;
  wasteLineUnifiedIds?: string[];
  /** Which section to scroll into view on open. Defaults to EPA-Requested Corrections (the primary surface). */
  initialSection?: DiscrepancySectionKey;
  /**
   * Explicit tenant for cross-tenant (staff) callers that aren't on a tenant-scoped
   * route (e.g. the staff Manifest Review queue). Defaults to the route's tenantId.
   */
  tenantId?: string;
  onClose: () => void;
  /**
   * Fired once, after the first row mutation of this dialog session succeeds
   * (resolve, dismiss, reopen, undo — single or bulk). Lets a caller whose own
   * `onClose` refetches whole-queue aggregates (the staff Manifest Review
   * page) skip that refetch on a close with no mutation, e.g. read-only
   * browsing. `onClose`'s signature is untouched: nine call sites render this
   * dialog and only the staff page wants the refetch.
   */
  onMutated?: () => void;
}

export function DiscrepancyReviewDialog({
  processedManifestIds,
  manifestTrackingNumber,
  wasteLineUnifiedIds,
  initialSection = "epaCorrection",
  tenantId: tenantIdProp,
  onClose,
  onMutated,
}: DiscrepancyReviewDialogProps) {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdProp ?? routeTenantId;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const showEpaCorrectionRows = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];

  const tableScrollRef = useCtrlWheelHorizontalScroll();

  const epaSectionRef = useRef<HTMLDivElement>(null);
  const missingSectionRef = useRef<HTMLDivElement>(null);
  const fieldSectionRef = useRef<HTMLDivElement>(null);
  const fieldIssueSectionRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);

  // Stable "now" so deadline math doesn't jitter across re-renders during the same dialog session.
  const nowRef = useMemo(() => new Date(), []);

  // Source of truth: the document the user asked to preview. Apollo's keyed useQuery discards
  // in-flight responses when the target changes, so a slow fetch can't clobber a newer selection.
  const [previewTarget, setPreviewTarget] = useState<{
    documentId: string;
    fileExtension: string;
  } | null>(null);

  const { data: previewUrlData, loading: previewUrlLoading } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      // `inline` so the same URL also renders when "Open in new tab" hands it
      // to the browser directly — attachment-signed URLs download instead.
      variables: { id: previewTarget?.documentId ?? "", inline: true },
      skip: !previewTarget,
      fetchPolicy: "network-only",
      onError: () => {
        // Clear the target so previewLoading doesn't hang the panel in its skeleton forever.
        alerts.error("Failed to load document preview");
        setPreviewTarget(null);
      },
    }
  );

  const previewDoc = useMemo(() => {
    if (!previewTarget) return null;
    if (previewUrlLoading) return null;
    const url = previewUrlData?.getDocumentDownloadLink;
    if (!url) return null;
    return {
      documentId: previewTarget.documentId,
      fileExtension: previewTarget.fileExtension,
      url,
    };
  }, [previewTarget, previewUrlLoading, previewUrlData]);

  const previewLoading = previewTarget != null && previewDoc == null;
  // Read three times below to size the dialog and mount the pane; computing it
  // once keeps the same question from costing three branches.
  const previewPaneOpen = previewDoc != null || previewLoading;

  // Always the target's id (not previewDoc's) so row highlights activate synchronously on click.
  const activePreviewDocId = previewTarget?.documentId ?? null;

  const openDocumentPreview = useCallback(
    (documentId: string, fileExtension: string | null | undefined) => {
      // Functional setter keeps the same-doc no-op guard without depending on previewTarget,
      // so the callback identity stays stable across preview changes.
      setPreviewTarget((prev) =>
        prev?.documentId === documentId
          ? prev
          : { documentId, fileExtension: fileExtension ?? "pdf" }
      );
    },
    []
  );

  const openDocumentInNewTab = useCallback(() => {
    if (!previewDoc?.url) return;
    window.open(previewDoc.url, "_blank");
  }, [previewDoc]);

  // Operation name strings refetch all active instances regardless of variable differences.
  // The expanders read a finding's event history from its own query, so every
  // mutation has to refetch it: otherwise the REOPENED or RESOLVED event the
  // reviewer just wrote never shows, because the expander stays mounted with
  // the same variables and Apollo serves the cached result.
  // Resolving the last open finding completes the manifest, and the writers
  // clear `blockedReason` in the same transaction (unblockCompletedManifests).
  // The header's chip and Unblock button read ManifestBlockState, so without
  // this refetch they keep showing a block the row action just cleared.
  const parentRefetchQueries = [
    "ProcessedManifests",
    "ComplianceManifestData",
    "DiscrepancyEvents",
    "ManifestBlockState",
  ];

  const [updateVendorData, { loading: updatingVendorData }] = useMutation(
    UPDATE_DISCREPANCY_VENDOR_DATA,
    { refetchQueries: parentRefetchQueries }
  );

  const [resolveExternally, { loading: resolvingExternally }] = useMutation(
    RESOLVE_DISCREPANCIES_EXTERNALLY,
    { refetchQueries: parentRefetchQueries }
  );

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_DISCREPANCIES, {
    // Refetch so the dismissed row updates to its Non-Discrepancy status —
    // it stays in the list, the resolver returns IGNORED rows same as any other.
    refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
  });

  const [reopenPending, { loading: reopening }] = useMutation(REOPEN_PENDING_DISCREPANCIES, {
    refetchQueries: parentRefetchQueries,
  });

  const [undoResolution, { loading: undoingResolution }] = useMutation(
    UNDO_RESOLVED_DISCREPANCIES,
    {
      // Undo also queues a recompute, which can add, reopen or auto-ignore
      // rows on this manifest — changes to the row *set*, which patching the
      // returned entities into the cache can't show. Refetch the list itself,
      // as dismiss does, or the section silently renders its pre-undo rows
      // until the reviewer reloads the page.
      //
      // The staff Manifest Review grid and summary tiles are SQL aggregates
      // the cache can't patch either; those refresh once per review session
      // when the staff page closes this dialog — and only if `onMutated`
      // fired, so a session with no row action never hits that refetch.
      refetchQueries: [...parentRefetchQueries, "BulkManifestDiscrepancies"],
    }
  );

  const mutationLoading = [
    updatingVendorData,
    resolvingExternally,
    dismissing,
    reopening,
    undoingResolution,
  ].some(Boolean);

  // Fires onMutated once per dialog session, on the first successful row
  // mutation, rather than once per action.
  const notifyMutated = useFireOnce(onMutated);

  // Non-null while the Mark blocked dialog is open (from the header button, or routed
  // from the resolve dialog's "Can't Resolve — Mark as Blocked" — which carries its note over).
  const [markBlockedRequest, setMarkBlockedRequest] = useState<{ note: string | null } | null>(
    null
  );

  // The rows the resolve modal is acting on — a section's selection (bulk
  // button) or a single row (row action). includeVendorAction is false for
  // Data Quality Issues (see RESOLVE_ACTION_DEFS).
  const [resolveTarget, setResolveTarget] = useState<{
    rows: Discrepancy[];
    includeVendorAction: boolean;
    clearSelection: () => void;
  } | null>(null);

  const { data, loading, refetch } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds },
  });

  const handleReopenPending = useCallback(
    async (discrepancy: Discrepancy) => {
      try {
        await reopenPending({ variables: { discrepancyIds: [discrepancy.id] } });
        notifyMutated();
        alerts.success("Discrepancy marked as pending review");
      } catch (err) {
        alerts.error(
          "Failed to mark discrepancy as pending review",
          err instanceof Error ? err : undefined
        );
      }
    },
    [reopenPending, notifyMutated, alerts]
  );

  const handleUndoResolution = useCallback(
    async (discrepancy: Discrepancy) => {
      try {
        await undoResolution({ variables: { discrepancyIds: [discrepancy.id] } });
        notifyMutated();
        alerts.success("Undone. The finding is unresolved.");
      } catch (err) {
        // The server refuses with an explanation (e.g. the row tracks an
        // e-Manifest correction request); the alert renders the error's message.
        // refetchQueries only fire on success; a refusal usually means the row
        // is stale (another tab undid it first), so refetch it.
        void refetch();
        alerts.error("Couldn't undo this.", err instanceof Error ? err : undefined);
      }
    },
    [undoResolution, notifyMutated, alerts, refetch]
  );

  useEffect(() => {
    if (loading || didInitialScrollRef.current) return;
    const targetRef = {
      epaCorrection: epaSectionRef,
      eManifestMissing: missingSectionRef,
      field: fieldSectionRef,
      fieldIssue: fieldIssueSectionRef,
    }[initialSection];
    // A section key outside the four above has no ref to scroll to. Stop
    // rather than re-running this effect on every render.
    if (!targetRef) {
      didInitialScrollRef.current = true;
      return;
    }
    // jsdom (unit tests) doesn't implement scrollIntoView — guard with a type check
    // rather than relying on the element existing.
    if (typeof targetRef.current?.scrollIntoView === "function") {
      targetRef.current.scrollIntoView({ block: "start" });
      didInitialScrollRef.current = true;
      return;
    }
    // The ref hasn't attached yet, but the target section might not be able
    // to render at all (flag off) — in that case there's nothing to wait
    // for, so stop retrying instead of leaving the effect to silently
    // re-run on every render forever.
    const isTargetRenderable = initialSection !== "epaCorrection" || showEpaCorrectionRows;
    if (!isTargetRenderable) {
      didInitialScrollRef.current = true;
    }
  }, [loading, initialSection, showEpaCorrectionRows]);

  const compliancePmIds = useMemo(() => {
    const raw = data?.bulkManifestDiscrepancies ?? [];
    const ids = new Set<string>();
    for (const d of raw) {
      if (isComplianceDiscrepancy(d)) {
        ids.add(d.processedManifestId);
      }
    }
    // Single-manifest mode needs this query for the header's context line
    // (generator, TSDF, ship date) even when nothing is a compliance row.
    if (processedManifestIds.length === 1) {
      ids.add(processedManifestIds[0]);
    }
    return [...ids];
  }, [data?.bulkManifestDiscrepancies, processedManifestIds]);

  const { data: complianceData } = useQuery(COMPLIANCE_MANIFEST_DATA_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${compliancePmIds.join("⁋")}`,
      page: 0,
      pageSize: compliancePmIds.length,
    },
    skip: compliancePmIds.length === 0 || !tenantId,
  });

  const complianceManifestMap = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof complianceData>["processedManifests"]["items"][number]
    >();
    for (const item of complianceData?.processedManifests?.items ?? []) {
      map.set(item.id, item);
    }
    return map;
  }, [complianceData]);

  const isSingleManifest = processedManifestIds.length === 1;

  // The header subtitle: generator + EPA id come off any discrepancy row for
  // this manifest; TSDF and ship date come off the compliance-manifest fetch
  // above (kept alive for single-manifest mode by the compliancePmIds fix).
  const headerContextLine = useMemo(() => {
    if (!isSingleManifest) return null;
    const anyRow = data?.bulkManifestDiscrepancies?.[0];
    const pm = complianceManifestMap.get(processedManifestIds[0]);
    return formatManifestContextLine({
      generatorName: anyRow?.facilityName,
      epaId: anyRow?.epaId,
      tsdfName: pm?.desFacilityName,
      shippedDate: pm?.shippedDate,
    });
  }, [
    isSingleManifest,
    data?.bulkManifestDiscrepancies,
    complianceManifestMap,
    processedManifestIds,
  ]);

  // The document behind "View document" in the title bar: the first row (in
  // query order) that carries a source document.
  const viewDocumentTarget = useMemo(() => {
    const row = (data?.bulkManifestDiscrepancies ?? []).find((d) => d.sourceDocumentId);
    return row?.sourceDocumentId
      ? { documentId: row.sourceDocumentId, fileExtension: row.sourceDocumentFileExtension }
      : null;
  }, [data?.bulkManifestDiscrepancies]);

  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [
        PROCESSED_MANIFESTS_QUERY,
        BULK_MANIFEST_DISCREPANCIES_QUERY,
        COMPLIANCE_MANIFEST_DATA_QUERY,
      ],
    }
  );

  const [showProofOfContactForPmId, setShowProofOfContactForPmId] = useState<string | null>(null);

  // The Missing e-Manifest row currently targeted by "Will never be in
  // e-Manifest" (staff-only). Non-null while that dialog is open.
  const [closeNeverInEManifestTarget, setCloseNeverInEManifestTarget] =
    useState<ComplianceItemMissing | null>(null);
  const onCloseNeverInEManifest = useCallback(
    (item: ComplianceItemMissing) => setCloseNeverInEManifestTarget(item),
    []
  );

  // Shared by EPA-Requested Corrections' and Missing e-Manifests' CompliancePane —
  // both compliance kinds resolve/report identically, only their `items` differ.
  const onResolveInEManifest = useCallback(
    async (discrepancyId: string) => {
      try {
        await resolveExternally({ variables: { discrepancyIds: [discrepancyId] } });
        // Same mutation executeResolveAction's "resolveExternally" branch
        // calls for Data Source Conflicts rows — it changes a discrepancy's
        // status here too (EPA-Requested Corrections / Missing e-Manifests),
        // so the staff queue can go stale the same way.
        notifyMutated();
        alerts.success("Marked as pending e-Manifest resolution");
      } catch {
        alerts.error("Failed to mark as pending");
      }
    },
    [resolveExternally, notifyMutated, alerts]
  );
  const onAddProofOfContact = useCallback((pmId: string) => setShowProofOfContactForPmId(pmId), []);
  const onCreateExceptionReport = useCallback(
    async (pmId: string) => {
      try {
        await createExceptionReport({ variables: { processedManifestId: pmId } });
        alerts.success("Exception report submission created");
      } catch {
        alerts.error("Failed to create exception report submission");
      }
    },
    [createExceptionReport, alerts]
  );

  const allDiscrepancies = useMemo(() => {
    // Auto-ignored rows (no resolvedBy — process-manifests dismissed a mismatch that
    // converged externally) are non-discrepancies with no review story to tell;
    // hide them entirely. Human-dismissed rows stay: they carry the reviewer's
    // "Non-Discrepancy" verdict.
    const raw = (data?.bulkManifestDiscrepancies ?? []).filter(
      (d) => !(d.status === "IGNORED" && !d.resolvedBy)
    );
    // Hide EPA correction rows when the flag is off, in case any were seeded during a flag-on window.
    const kindFiltered = showEpaCorrectionRows
      ? raw
      : raw.filter((d) => d.kind !== DiscrepancyKind.EpaCorrectionRequest);
    if (!wasteLineUnifiedIds) return kindFiltered;
    const wluSet = new Set(wasteLineUnifiedIds);
    return kindFiltered.filter((d) => d.wasteLineUnifiedId && wluSet.has(d.wasteLineUnifiedId));
  }, [data?.bulkManifestDiscrepancies, wasteLineUnifiedIds, showEpaCorrectionRows]);

  const epaCorrectionItems = useMemo<ComplianceItem[]>(() => {
    const items: ComplianceItem[] = [];
    for (const d of allDiscrepancies) {
      if (d.kind !== DiscrepancyKind.EpaCorrectionRequest) continue;
      const status = getDiscrepancyStatus(d);
      const epa = mapEpaCorrectionDiscrepancy({ ...d, status });
      if (epa) {
        // The pure helper doesn't see Apollo data; apply tsdfName from the compliance pm fetch.
        const pm = complianceManifestMap.get(d.processedManifestId);
        items.push({ ...epa, tsdfName: pm?.desFacilityName ?? null });
      }
    }
    return items;
  }, [allDiscrepancies, complianceManifestMap]);

  const missingEManifestItems = useMemo<ComplianceItem[]>(() => {
    const items: ComplianceItem[] = [];
    for (const d of allDiscrepancies) {
      // EPA correction rows take priority (mirrors the DB's GENERATED kind
      // column, which resolves EPA_CORRECTION_REQUEST first) — without this,
      // a row with both wasteManifestCorrectionRequestId and escalation set
      // would double-count into both sections.
      if (d.kind === DiscrepancyKind.EpaCorrectionRequest) continue;
      if (!isComplianceDiscrepancy(d)) continue;
      const status = getDiscrepancyStatus(d);
      const pm = complianceManifestMap.get(d.processedManifestId);
      const proofDoc = pm?.documents?.find(
        (link) => link.document.documentType === DocumentType.WasteTransporterContactProof
      )?.document;
      const missing = mapMissingEManifestDiscrepancy(
        { ...d, status },
        {
          tsdfName: pm?.desFacilityName ?? null,
          shippedDate: pm?.shippedDate ?? null,
          transporterContactedAt: pm?.transporterContactedAt ?? null,
          transporterContactedBy: pm?.transporterContactedBy ?? null,
          transporterContactProofDocumentId: proofDoc?.id ?? null,
          exceptionReportSubmissionId: pm?.exceptionReportSubmission?.id ?? null,
          exceptionReportSubmissionStatus: pm?.exceptionReportSubmission?.status ?? null,
          exceptionReportTitle: pm?.exceptionReportSubmission?.title ?? null,
        }
      );
      items.push(missing);
    }
    return items;
  }, [allDiscrepancies, complianceManifestMap]);

  // Every section shows every status now — no filter to apply. These two
  // names stay (rather than renaming every call site below) because the
  // status filter used to live here; `hasUrgent` still excludes resolved
  // items to flag only what's actually time-critical.
  const filteredEpaCorrectionItems = epaCorrectionItems;
  const filteredMissingEManifestItems = missingEManifestItems;

  const epaHasUrgent = useMemo(
    () =>
      filteredEpaCorrectionItems.some(
        (i) => i.status !== "resolved" && getDeadlineBand(i, nowRef) === "crit"
      ),
    [filteredEpaCorrectionItems, nowRef]
  );
  const missingHasUrgent = useMemo(
    () =>
      filteredMissingEManifestItems.some(
        (i) => i.status !== "resolved" && getDeadlineBand(i, nowRef) === "crit"
      ),
    [filteredMissingEManifestItems, nowRef]
  );

  const isLineItemMode = Boolean(wasteLineUnifiedIds);

  let title: string;
  if (isLineItemMode) {
    title = `Line Item Discrepancies — ${wasteLineUnifiedIds?.length ?? 0} line items`;
  } else if (isSingleManifest) {
    title = `Manifest discrepancies — ${manifestTrackingNumber ?? processedManifestIds[0].slice(0, 8)}`;
  } else {
    title = `Bulk Discrepancy Review — ${processedManifestIds.length} manifests`;
  }

  // Only kind=FIELD_DISCREPANCY rows belong in Data Source Conflicts; other
  // kinds render elsewhere.
  const allFieldDiscrepancies = useMemo(
    () => allDiscrepancies.filter((d) => d.kind === DiscrepancyKind.FieldDiscrepancy),
    [allDiscrepancies]
  );
  const allFieldIssueDiscrepancies = useMemo(
    () => allDiscrepancies.filter((d) => d.kind === DiscrepancyKind.FieldIssue),
    [allDiscrepancies]
  );

  const discrepancies = allFieldDiscrepancies;
  const fieldIssues = allFieldIssueDiscrepancies;

  /*
   * Display order, built here rather than in each pane, because
   * `useRangeSelection` resolves a shift-click range by index and has to index
   * the same order the reader sees.
   *
   * The panes used to sort their own copy while the hook indexed the unsorted
   * array the parent passed. Given source order [L1 unresolved, L2 resolved,
   * L3 unresolved] a pane rendered L1, L3, L2, so shift-clicking the two
   * visually adjacent rows L1 and L3 resolved source indices 0..2 and selected
   * L2 as well.
   */
  const conflictGroups = useMemo(() => buildFieldGroups(discrepancies), [discrepancies]);
  // Counted over every row in the section, not the current selection: the
  // warning is there to be read before anyone picks rows.
  const unmatchableLines = useMemo(() => summarizeUnmatchableLines(discrepancies), [discrepancies]);
  // Keeps the notice, and the way back into the matching screen, after a run
  // has applied a pairing and taken the marks off.
  const settledManifestIds = useMemo(
    () => manifestsWithSettledWasteLineMatch(discrepancies),
    [discrepancies]
  );

  /*
   * The manifest whose waste lines are being matched, or null when the dialog
   * is closed. Held by id rather than a boolean so the dialog knows which
   * manifest it is for when the review covers several.
   */
  const [matchingManifest, setMatchingManifest] = useState<WasteLineMatchingTarget | null>(null);

  /*
   * Manifests whose waste lines were matched while this dialog has been open.
   *
   * vendorAlignmentSuspect is written by process-manifests, so it stays true
   * until that run finishes and the rows still read as unmatchable in the
   * meantime. Without this the warning is unchanged by the thing that answers
   * it, which reads as the save having done nothing. Session-local on purpose:
   * by the time a reader comes back to this manifest the run has been through
   * and the flags speak for themselves.
   */
  const [justMatchedIds, setJustMatchedIds] = useState<ReadonlySet<string>>(new Set<string>());
  const onMatchingSaved = useCallback(
    (processedManifestId: string) => {
      setJustMatchedIds((prev) => new Set(prev).add(processedManifestId));
      notifyMutated();
      void refetch();
    },
    [notifyMutated, refetch]
  );
  // Removing a pairing is the opposite of matching it, not a second way to
  // match it: the manifest goes back to line-number pairing, so it must not
  // read as "Waste lines matched" here just because a save would have.
  const onMatchingRemoved = useCallback(
    (processedManifestId: string) => {
      setJustMatchedIds((prev) => {
        if (!prev.has(processedManifestId)) return prev;
        const next = new Set(prev);
        next.delete(processedManifestId);
        return next;
      });
      notifyMutated();
      void refetch();
    },
    [notifyMutated, refetch]
  );

  // Undefined when nothing in this list has a vendor-side count mismatch, which
  // hides the button rather than offering one that opens an empty dialog.
  // Skips manifests already in justMatchedIds so a bulk review advances past
  // one already matched instead of reopening it on every click.
  const matchingTarget = useMemo(
    () => findWasteLineMatchingTarget(discrepancies, justMatchedIds),
    [discrepancies, justMatchedIds]
  );
  const openMatching = useMemo(() => {
    if (!matchingTarget) return undefined;
    return () => setMatchingManifest(matchingTarget);
  }, [matchingTarget]);
  const conflictsInDisplayOrder = useMemo(
    () => conflictGroups.flatMap((group) => group.items),
    [conflictGroups]
  );
  const fieldIssuesInDisplayOrder = useMemo(() => sortTerminalLast(fieldIssues), [fieldIssues]);

  const {
    selectedIds: fieldSelectedIds,
    toggle: toggleFieldSelect,
    clear: clearFieldSelection,
  } = useRangeSelection({ items: conflictsInDisplayOrder });

  const {
    selectedIds: fieldIssueSelectedIds,
    toggle: toggleFieldIssueSelect,
    clear: clearFieldIssueSelection,
  } = useRangeSelection({ items: fieldIssuesInDisplayOrder });

  // The resolve modal's options: the caller-appropriate action definitions
  // with eligibility computed against the targeted rows.
  const resolveOptions = useMemo(() => {
    if (!resolveTarget) return [];
    const actions: ActionKey[] = resolveTarget.includeVendorAction
      ? ["updateVendorData", "resolveExternally", "nonDiscrepancy"]
      : ["resolveExternally", "nonDiscrepancy"];
    // Blocking is manifest-level and staff-only, so the escape hatch is offered only
    // when this dialog is reviewing exactly one manifest as staff.
    if (isStaff && isSingleManifest) {
      actions.push("cantDetermine");
    }
    return actions.map((action) => {
      const { eligibleIds, skips } = computeActionEligibility(action, resolveTarget.rows);
      return {
        action,
        ...RESOLVE_ACTION_DEFS[action],
        eligibleIds,
        skipSummary: skips.length > 0 ? formatSkipSummary(skips) : undefined,
      };
    });
  }, [resolveTarget, isStaff, isSingleManifest]);

  const executeResolveAction = useCallback(
    async (action: ActionKey, eligibleIds: string[], note: string | null) => {
      if (!resolveTarget) return;
      // "Can't Resolve — Mark as Blocked" routes to the Mark blocked dialog instead of running a
      // mutation; the selection stays (nothing was resolved) and the note carries over.
      if (action === "cantDetermine") {
        setResolveTarget(null);
        setMarkBlockedRequest({ note });
        return;
      }
      const { label } = RESOLVE_ACTION_DEFS[action];
      try {
        if (action === "updateVendorData") {
          const { data } = await updateVendorData({
            variables: { discrepancyIds: eligibleIds, note },
          });
          const { severity, message } = vendorDataUpdatedAlert(
            data?.updateDiscrepancyVendorData ?? []
          );
          alerts[severity](message);
        } else if (action === "resolveExternally") {
          await resolveExternally({ variables: { discrepancyIds: eligibleIds, note } });
          alerts.success(
            countedDiscrepancyAlert(eligibleIds.length, "marked as pending e-Manifest resolution")
          );
        } else {
          await dismiss({ variables: { discrepancyIds: eligibleIds, note } });
          alerts.success(
            eligibleIds.length === 1
              ? "1 item dismissed as a non-discrepancy"
              : `${eligibleIds.length} items dismissed as non-discrepancies`
          );
        }
        notifyMutated();
        resolveTarget.clearSelection();
      } catch (err) {
        alerts.error(`Failed to ${label.toLowerCase()}`, err instanceof Error ? err : undefined);
      }
      setResolveTarget(null);
    },
    [resolveTarget, updateVendorData, resolveExternally, dismiss, notifyMutated, alerts]
  );

  const [collapsedSections, setCollapsedSections] = useState<Set<DiscrepancySectionKey>>(new Set());
  const toggleSectionCollapsed = (key: DiscrepancySectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectedFieldDiscrepancies = useMemo(
    () => discrepancies.filter((d) => fieldSelectedIds.has(d.id)),
    [discrepancies, fieldSelectedIds]
  );
  const selectedFieldIssueDiscrepancies = useMemo(
    () => fieldIssues.filter((d) => fieldIssueSelectedIds.has(d.id)),
    [fieldIssues, fieldIssueSelectedIds]
  );

  return (
    <>
      <Dialog
        open
        // Ignore backdrop/Escape while a row mutation is in flight. A close
        // here would run before the mutation's `notifyMutated()` call, so
        // `onMutated` would never fire for this action and a caller whose
        // `onClose` refetches on it (the staff page) would see pre-mutation
        // counts and skip the refetch entirely. The row actions are disabled
        // for the same window, so this only defers the close by one request.
        onClose={() => {
          if (mutationLoading) return;
          onClose();
        }}
        maxWidth={previewPaneOpen ? false : "xl"}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxHeight: "90vh",
              height: "90vh",
              ...(previewPaneOpen ? { maxWidth: "95vw", width: "95vw" } : {}),
            },
          },
        }}
        title={title}
        noPrimaryAction
        titleBarActionElement={
          <DialogTitleBarActions
            isStaff={isStaff}
            isSingleManifest={isSingleManifest}
            processedManifestId={processedManifestIds[0]}
            manifestTrackingNumber={manifestTrackingNumber}
            viewDocumentTarget={viewDocumentTarget}
            onViewDocument={openDocumentPreview}
            markBlockedOpen={markBlockedRequest !== null}
            markBlockedInitialNote={markBlockedRequest?.note}
            onOpenMarkBlocked={() => setMarkBlockedRequest({ note: null })}
            onCloseMarkBlocked={() => setMarkBlockedRequest(null)}
            onBlocked={onClose}
          />
        }
        contentSx={{ overflow: "hidden" }}
        // The grey panel below runs to the dialog's own edges and carries its
        // own gutters, so the content area contributes none. With DialogContent's
        // default 24px the panel showed as a 16px sliver either side of the
        // cards rather than as a ground behind them.
        DialogContentProps={{ sx: { px: 0 } }}
      >
        {/* The Dialog wrapper has no subtitle slot styled as caption/text.secondary,
            so the context line renders as the first element of the content area
            instead of changing that shared component's contract. */}
        <HeaderContextLine
          text={headerContextLine}
          trailing={
            <HeaderBlockedChip
              isStaff={isStaff}
              isSingleManifest={isSingleManifest}
              processedManifestId={processedManifestIds[0]}
            />
          }
        />
        {/* position: relative wrapper gives children a concrete height to resolve against. */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
              }}
            >
              <Box
                ref={tableScrollRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  // The scroller sits outside the grey card below, so the
                  // scrollbar runs down the dialog rather than inside the
                  // card's rounded corners.
                  overflow: "auto",
                  // A 6px gutter above a pinned section title, in the panel's
                  // own colour, so the title reads as sitting on the grey
                  // ground instead of welded to the header above it.
                  //
                  // A border, not padding: `overflow: auto` clips at the
                  // padding box, so rows scroll through a padding-top — a
                  // column header was measured sliding through it. The border
                  // is outside that box, so content can never reach it, and
                  // sticky `top: 0` resolves to just below it. Invisible at
                  // rest, where it continues the panel's own grey.
                  borderTop: (theme) => `6px solid ${theme.palette.canvas}`,
                }}
              >
                <Box
                  sx={{
                    // Fills the scroller when the sections are short, and grows
                    // past it when they are tall or a table scrolls sideways.
                    flexGrow: 1,
                    width: "fit-content",
                    minWidth: "100%",
                    // A 24px inset on all four sides, matching the dialog
                    // title's: 18 here plus the scroller's 6px gutter border
                    // above, the cards' own mx on the sides, and 8 plus the
                    // last card's 16px mb at the bottom.
                    pt: 2.25,
                    pb: 1,
                    // The app ground, so the section cards (which sit on
                    // background.paper) read as separate blocks with a darker
                    // gutter between them. Not background.default: the light
                    // palette never repoints it, so it and paper are both
                    // #fff and the gutters would only show in dark mode.
                    bgcolor: (theme) => theme.palette.canvas,
                  }}
                >
                  {showEpaCorrectionRows && (
                    <DiscrepancySection
                      ref={epaSectionRef}
                      title="EPA-Requested Corrections"
                      count={filteredEpaCorrectionItems.length}
                      countLabel={getStatusBreakdownLabel(filteredEpaCorrectionItems)}
                      loading={loading}
                      hasUrgent={epaHasUrgent}
                      collapsed={collapsedSections.has("epaCorrection")}
                      onToggleCollapse={() => toggleSectionCollapsed("epaCorrection")}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ...SECTION_DESCRIPTION_SX }}
                      >
                        EPA-Requested Corrections are open requests from EPA or a state regulator to
                        correct a submitted e-Manifest, each with its own 30-day response deadline.
                      </Typography>
                      <EpaCorrectionsPane
                        items={filteredEpaCorrectionItems}
                        now={nowRef}
                        loading={loading}
                        activePreviewDocId={activePreviewDocId}
                        onViewDocument={openDocumentPreview}
                        onResolveInEManifest={onResolveInEManifest}
                      />
                    </DiscrepancySection>
                  )}

                  <DiscrepancySection
                    ref={missingSectionRef}
                    title="Missing e-Manifests"
                    count={filteredMissingEManifestItems.length}
                    countLabel={getStatusBreakdownLabel(filteredMissingEManifestItems)}
                    loading={loading}
                    hasUrgent={missingHasUrgent}
                    collapsed={collapsedSections.has("eManifestMissing")}
                    onToggleCollapse={() => toggleSectionCollapsed("eManifestMissing")}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ...SECTION_DESCRIPTION_SX }}
                    >
                      Missing e-Manifests are shipments with no matching e-Manifest record once
                      RCRA&apos;s 45- and 60-day compliance thresholds pass.
                    </Typography>
                    <MissingEManifestPane
                      items={filteredMissingEManifestItems}
                      now={nowRef}
                      loading={loading}
                      activePreviewDocId={activePreviewDocId}
                      creatingExceptionReport={creatingExceptionReport}
                      onViewDocument={openDocumentPreview}
                      onResolveInEManifest={onResolveInEManifest}
                      onAddProofOfContact={onAddProofOfContact}
                      onCreateExceptionReport={onCreateExceptionReport}
                      tenantId={tenantId}
                      isStaff={isStaff}
                      onCloseNeverInEManifest={onCloseNeverInEManifest}
                    />
                  </DiscrepancySection>

                  <DiscrepancySection
                    ref={fieldSectionRef}
                    title="Data Source Conflicts"
                    count={discrepancies.length}
                    countLabel={getStatusBreakdownLabel(
                      discrepancies.map((d) => ({ status: getDiscrepancyStatus(d) }))
                    )}
                    loading={loading}
                    collapsed={collapsedSections.has("field")}
                    onToggleCollapse={() => toggleSectionCollapsed("field")}
                    selectedCount={fieldSelectedIds.size}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ...SECTION_DESCRIPTION_SX }}
                    >
                      Data Source Conflicts surface when a specific value disagrees between vendor
                      data, EPA e-Manifest, and the manifest document — e.g. waste codes, generator
                      info, quantity. Resolve them by updating the vendor record or acknowledging
                      the difference.
                    </Typography>
                    <UnmatchableLinesNotice
                      summary={unmatchableLines}
                      settledManifestIds={settledManifestIds}
                      justMatchedIds={justMatchedIds}
                      onMatchLines={openMatching}
                    />
                    <DiscrepancyBulkActionBar
                      totalSelected={fieldSelectedIds.size}
                      loading={mutationLoading}
                      onOpen={() =>
                        setResolveTarget({
                          rows: selectedFieldDiscrepancies,
                          includeVendorAction: true,
                          clearSelection: clearFieldSelection,
                        })
                      }
                    />
                    <DataSourceConflictsPane
                      fieldGroups={conflictGroups}
                      loading={loading}
                      selectedIds={fieldSelectedIds}
                      onToggleSelect={toggleFieldSelect}
                      showManifestColumns={!isSingleManifest}
                      activePreviewDocId={activePreviewDocId}
                      onViewDocument={openDocumentPreview}
                      isStaff={isStaff}
                      onResolve={(d) =>
                        setResolveTarget({
                          rows: [d],
                          includeVendorAction: true,
                          clearSelection: () => {},
                        })
                      }
                      mutationLoading={mutationLoading}
                      onMarkUnresolved={(d) => void handleReopenPending(d)}
                      onUndoResolution={(d) => void handleUndoResolution(d)}
                    />
                  </DiscrepancySection>

                  <DiscrepancySection
                    ref={fieldIssueSectionRef}
                    title="Data Quality Issues"
                    count={fieldIssues.length}
                    countLabel={getStatusBreakdownLabel(
                      fieldIssues.map((d) => ({ status: getDiscrepancyStatus(d) }))
                    )}
                    loading={loading}
                    collapsed={collapsedSections.has("fieldIssue")}
                    onToggleCollapse={() => toggleSectionCollapsed("fieldIssue")}
                    selectedCount={fieldIssueSelectedIds.size}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ...SECTION_DESCRIPTION_SX }}
                    >
                      Data Quality Issues flag values that look wrong on their own — the value
                      itself fails a check, like an invalid Texas waste code format. Resolve or
                      dismiss each issue.
                    </Typography>
                    <DiscrepancyBulkActionBar
                      totalSelected={fieldIssueSelectedIds.size}
                      loading={mutationLoading}
                      onOpen={() =>
                        setResolveTarget({
                          rows: selectedFieldIssueDiscrepancies,
                          includeVendorAction: false,
                          clearSelection: clearFieldIssueSelection,
                        })
                      }
                    />
                    <FieldIssuesPane
                      items={fieldIssues}
                      loading={loading}
                      selectedIds={fieldIssueSelectedIds}
                      onToggleSelect={toggleFieldIssueSelect}
                      showManifestColumns={!isSingleManifest}
                      isStaff={isStaff}
                      onResolve={(d) =>
                        setResolveTarget({
                          rows: [d],
                          includeVendorAction: false,
                          clearSelection: () => {},
                        })
                      }
                      mutationLoading={mutationLoading}
                      onMarkUnresolved={(d) => void handleReopenPending(d)}
                      onUndoResolution={(d) => void handleUndoResolution(d)}
                    />
                  </DiscrepancySection>
                </Box>
              </Box>

              {previewPaneOpen && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 2,
                        // Match the column header height so the preview top aligns with the first data row.
                        height: STICKY_COLUMN_HEADER_HEIGHT,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: subtleSurfaceBg,
                      }}
                    >
                      <Typography variant="subtitle2">Source Document</Typography>
                      <Stack direction="row" spacing={0.5}>
                        {previewDoc && (
                          <Tooltip title="Open in new tab">
                            <IconButton size="small" onClick={openDocumentInNewTab}>
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Close preview">
                          <IconButton size="small" onClick={() => setPreviewTarget(null)}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      {previewLoading ? (
                        <Skeleton
                          variant="rectangular"
                          animation="wave"
                          height="100%"
                          width="100%"
                        />
                      ) : previewDoc ? (
                        <FilePreview
                          fileUrl={previewDoc.url}
                          fileExtension={previewDoc.fileExtension}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Dialog>

      {showProofOfContactForPmId && (
        <ProofOfContactDialog
          open
          onClose={() => setShowProofOfContactForPmId(null)}
          processedManifestId={showProofOfContactForPmId}
          tenantId={tenantId}
        />
      )}

      <CloseNeverInEManifestDialogSlot
        target={closeNeverInEManifestTarget}
        onClose={() => setCloseNeverInEManifestTarget(null)}
        onClosed={notifyMutated}
      />

      <WasteLinePairingDialog
        target={matchingManifest}
        onClose={() => setMatchingManifest(null)}
        onSaved={onMatchingSaved}
        onRemoved={onMatchingRemoved}
      />

      <ResolveDiscrepanciesDialog
        open={resolveTarget !== null}
        totalCount={resolveTarget?.rows.length ?? 0}
        options={resolveOptions}
        loading={mutationLoading}
        singleDiscrepancyId={getSingleDiscrepancyId(resolveTarget)}
        onClose={() => setResolveTarget(null)}
        onConfirm={executeResolveAction}
      />
    </>
  );
}
